import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  PasswordInput,
  Button,
  Form,
  Stack,
  InlineNotification,
  Theme,
  Tile,
} from '@carbon/react';
import logo from '../assets/logo.png';
import ChristmasDecor from './ChristmasDecor';
import './Login.css';

/**
 * Check if current date is in Christmas period (Dec 10 - Jan 8)
 */
const isChristmasSeason = () => {
  const now = new Date();
  const month = now.getMonth() + 1; // 0-indexed, so +1
  const day = now.getDate();

  // December 10-31 or January 1-8
  return (month === 12 && day >= 10) || (month === 1 && day <= 8);
};

/**
 * Password strength validation
 * Requires at least one number and one special character
 */
const validatePasswordStrength = (password) => {
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password);
  return hasNumber && hasSpecialChar;
};

/**
 * Set Password Page Component
 * Shown when a user logs in for the first time (hasSetHisPassword = false).
 * Uses the exact same layout as the Login page, but with password + confirm fields.
 */
const SetPassword = () => {
  const navigate = useNavigate();
  const { setPassword, logout } = useAuth();
  const { theme } = useTheme();
  const isChristmas = isChristmasSeason();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear field error when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: '',
      }));
    }

    // Clear server error
    if (serverError) {
      setServerError('');
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Lozinka je obavezna';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Lozinka mora imati najmanje 8 karaktera';
    } else if (!validatePasswordStrength(formData.password)) {
      newErrors.password = 'Lozinka mora sadržati broj i specijalni karakter';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Potvrdite lozinku';
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Lozinke se ne poklapaju';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setServerError('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call set password from auth context
      await setPassword(formData.password, formData.confirmPassword);

      // On success, clear the old token and redirect back to login
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      // Display error message
      setServerError(error.message || 'Postavljanje lozinke nije uspelo. Pokušajte ponovo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Theme theme={theme}>
      {isChristmas && <ChristmasDecor />}
      <div className="login-container">
        <div className="login-content">
          {/* Logo */}
          <div className="login-logo-container">
            <img src={logo} alt="Logo" className="login-logo" />
          </div>

          {/* Set Password Card */}
          <Tile className="login-tile">
            {/* Header */}
            <div className="login-header">
              <h1 className="login-title">Postavi lozinku</h1>
              <p className="login-subtitle">
                {isChristmas ? 'Srećne praznike! Postavite lozinku za svoj nalog' : 'Postavite lozinku za svoj nalog'}
              </p>
            </div>

            {/* Server Error Message */}
            {serverError && (
              <div style={{
                position: 'fixed',
                top: '4rem',
                right: '1rem',
                zIndex: 9999,
                minWidth: '320px',
                animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <style>
                  {`
                    @keyframes slideInFromRight {
                      from {
                        transform: translateX(calc(100% + 1rem));
                        opacity: 0;
                      }
                      to {
                        transform: translateX(0);
                        opacity: 1;
                      }
                    }
                  `}
                </style>
                <InlineNotification
                  kind="error"
                  title="Greška"
                  subtitle={serverError}
                  lowContrast
                  hideCloseButton={false}
                  onCloseButtonClick={() => setServerError('')}
                />
              </div>
            )}

            {/* Set Password Form */}
            <Form onSubmit={handleSubmit} className="login-form">
              <Stack gap={5}>
                {/* Password Field */}
                <PasswordInput
                  id="password"
                  labelText="Lozinka"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  invalid={!!errors.password}
                  invalidText={errors.password}
                  disabled={isLoading}
                  autoComplete="new-password"
                  size="lg"
                  helperText="Lozinka mora sadržati najmanje 8 karaktera, jedan broj i jedan specijalni karakter"
                />

                {/* Confirm Password Field */}
                <PasswordInput
                  id="confirmPassword"
                  labelText="Potvrdi lozinku"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  invalid={!!errors.confirmPassword}
                  invalidText={errors.confirmPassword}
                  disabled={isLoading}
                  autoComplete="new-password"
                  size="lg"
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                  className="login-button"
                >
                  {isLoading ? 'Postavljanje...' : 'Postavi lozinku'}
                </Button>
              </Stack>
            </Form>
          </Tile>
        </div>
      </div>
    </Theme>
  );
};

export default SetPassword;
